import { Button, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CodedException,
  CommunityPermissions,
  PersonalizationNotCompletedPayload,
  PricingNotCompletedPayload,
} from '@/core';
import { useCommunityContext } from '@/views';
import {
  ProjectStepLookupState,
  useGetFloorplanDetailsByHomeQuery,
  useGetProjectHomeInfoQuery,
  useGetProjectHomeStepsQuery,
  useUpdateFinalizationStateMutation,
} from '@/redux';
import { useCallback, useMemo, useState } from 'react';
import { confirmation, notify, useAppFindFormik } from '@/components';
import { RtkqNotifyError } from '@/redux/rtkq';
import { PricingNotCompletedModal } from './PricingNotCompletedModal';
import { PersonalizationNoItemsSelectedInRoomModal } from './PersonalizationNoItemsSelectedInRoomModal';
import { PersonalizationNoUnfinalizePermissionModal } from './PersonalizationNoUnfinalizePermissionModal';
import { FormHomeDeadlines } from './FormHomeDeadlines';
import { PersonalizationNotCompletedModal } from './PersonalizationNotCompletedModal';

interface Props {
  projectId: string;
  homeId: string;
  selectedStepId: string;
}

const useUpdateFinalizationState = (
  props: Props,
  onPricingNotCompleted: (payload: PricingNotCompletedPayload) => any,
  onPersonalizationNotCompleted: (
    payload: PersonalizationNotCompletedPayload,
  ) => any,
  onPersonalizationNoSelectedItemsModal: (value: boolean) => any,
  onPersonalizationNoUnfinalizePermissionModal: (value: boolean) => any,
) => {
  const [update] = useUpdateFinalizationStateMutation();
  const { homeId, projectId, selectedStepId } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });
  const { data: steps } = useGetProjectHomeStepsQuery({ homeId, projectId });
  const form = useAppFindFormik('home-deadlines');

  return useCallback(
    (currentValue: boolean) => {
      const handleError = (error: any) => {
        const e = CodedException.from(error);
        if (e.code === 'INV.ROOM_PERSONALIZATION.NO_ITEMS_SELECTED') {
          onPersonalizationNoSelectedItemsModal(true);
          return;
        }

        if (e.code === 'INV.PERSONALIZATION_NOT_COMPLETED') {
          onPersonalizationNotCompleted(e.payload);
          return;
        }

        if (e.code === 'INV.PRICING_NOT_COMPLETED') {
          onPricingNotCompleted(e.payload);
          return;
        }

        if (e.code === 'INV.ROOM_PERSONALIZATION.NO_UNFINALIZE_PERMISSION') {
          onPersonalizationNoUnfinalizePermissionModal(true);
          return;
        }

        throw error;
      };
      const perform = ({ steps }: { steps?: ProjectStepLookupState[] }) =>
        update({
          homeId,
          projectId,
          value: !currentValue,
          stepId: selectedStepId,
          deadlines: steps?.map((step) => ({
            stepId: step.id,
            date: step.deadlineDate,
          })),
          [RtkqNotifyError]: false,
        })
          .unwrap()
          .then(() => notify.success.t('personalization.summary.updated'))
          .catch(handleError);

      confirmation.show({
        title: currentValue ? t('unFinalize.title') : t('finalize.title'),
        body: currentValue ? (
          <>
            {t('unFinalize.confirmation')}
            <Divider />
            <FormHomeDeadlines steps={steps!} onSubmit={perform} />
          </>
        ) : (
          t('finalize.confirmation')
        ),
        onConfirm: () => {
          currentValue
            ? form?.current?.submitForm()
            : perform({ steps: undefined });
        },
      });
    },
    [
      t,
      steps,
      onPersonalizationNoSelectedItemsModal,
      onPersonalizationNotCompleted,
      onPricingNotCompleted,
      onPersonalizationNoUnfinalizePermissionModal,
      update,
      homeId,
      projectId,
      selectedStepId,
      form,
    ],
  );
};

export const FinalizeButton = (props: Props) => {
  const { t } = useTranslation();
  const { projectId, homeId, selectedStepId } = props;
  const { data: context } = useGetProjectHomeInfoQuery({
    id: projectId,
    homeId,
  });
  const { data: floorplan } = useGetFloorplanDetailsByHomeQuery({
    homeId,
  });
  const [pricingNotCompletedPayload, setPricingNotCompletedPayload] =
    useState<PricingNotCompletedPayload>();
  const [
    personalizationNotCompletedPayload,
    setPersonalizationNotCompletedPayload,
  ] = useState<PersonalizationNotCompletedPayload>();
  const [
    personalizationNoSelectedItemsModal,
    setPersonalizationNoSelectedItemsModal,
  ] = useState<boolean>();
  const [
    personalizationNoUnfinalizePermissionModal,
    setPersonalizationNoUnfinalizePermissionModal,
  ] = useState<boolean>();
  const handleFinalization = useUpdateFinalizationState(
    props,
    setPricingNotCompletedPayload,
    setPersonalizationNotCompletedPayload,
    setPersonalizationNoSelectedItemsModal,
    setPersonalizationNoUnfinalizePermissionModal,
  );
  const { assert } = useCommunityContext();
  const step = context?.steps.find((x) => x.stepId === selectedStepId);

  const title = useMemo(
    () =>
      step?.isFinalized
        ? t('personalization.summary.unFinalize.title')
        : t('personalization.summary.finalize.title'),
    [t, step],
  );

  const enable =
    step?.isFinalized ||
    assert(CommunityPermissions.Personalization.Manage, true);

  if (!floorplan || !context || !step || !enable) return null;

  return (
    <>
      <Button
        onClick={() => handleFinalization(step!.isFinalized)}
        type={step.isFinalized ? 'primary' : undefined}
      >
        {title}
      </Button>
      {personalizationNoSelectedItemsModal && (
        <PersonalizationNoItemsSelectedInRoomModal
          onClose={() => setPersonalizationNoSelectedItemsModal(false)}
        />
      )}
      {pricingNotCompletedPayload && (
        <PricingNotCompletedModal
          errorPayload={pricingNotCompletedPayload}
          onClose={() => setPricingNotCompletedPayload(undefined)}
        />
      )}
      {personalizationNotCompletedPayload && (
        <PersonalizationNotCompletedModal
          errorPayload={personalizationNotCompletedPayload}
          onClose={() => setPersonalizationNotCompletedPayload(undefined)}
        />
      )}
      {personalizationNoUnfinalizePermissionModal && (
        <PersonalizationNoUnfinalizePermissionModal
          onClose={() => setPersonalizationNoUnfinalizePermissionModal(false)}
        />
      )}
    </>
  );
};
